import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { referralPartnerApi } from '@/api/referralPartner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

type HospitalPartnerRow = {
  partner: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    status: string;
  };
  leadCount: number;
};

export default function HospitalPartnerManagementPage() {
  const qc = useQueryClient();
  const [selectedPartnerId, setSelectedPartnerId] = useState<string>('');
  const [reportFrom, setReportFrom] = useState('');
  const [reportTo, setReportTo] = useState('');

  const [rateReason, setRateReason] = useState<string>('');
  const [editRatePoints, setEditRatePoints] = useState<Record<'NEW' | 'OPD_VISITED' | 'IPD_ADMITTED', string>>({
    NEW: '',
    OPD_VISITED: '',
    IPD_ADMITTED: '',
  });

  const [leadId, setLeadId] = useState<string>('');
  const [leadMilestone, setLeadMilestone] = useState<'NEW' | 'OPD_VISITED' | 'IPD_ADMITTED'>('OPD_VISITED');
  const [leadPoints, setLeadPoints] = useState<string>('0');
  const [leadReason, setLeadReason] = useState<string>('');
  const [payoutRefById, setPayoutRefById] = useState<Record<string, string>>({});
  const [rupeePerPoint, setRupeePerPoint] = useState('1');

  const { data: partners = [], isLoading: partnersLoading, error: partnersError } = useQuery({
    queryKey: ['hospital-partners'],
    queryFn: async () => {
      const res = await referralPartnerApi.admin.hospital.partners();
      return (res.data?.data ?? []) as HospitalPartnerRow[];
    },
  });

  const {
    data: partnerRates,
    isLoading: ratesLoading,
    error: ratesError,
  } = useQuery({
    queryKey: ['hospital-partner-rates', selectedPartnerId],
    queryFn: async () => {
      const res = await referralPartnerApi.admin.hospital.partnerRates(selectedPartnerId);
      return res.data?.data;
    },
    enabled: !!selectedPartnerId,
  });

  const rates = (partnerRates?.rates ?? []) as Array<{
    milestone: 'NEW' | 'OPD_VISITED' | 'IPD_ADMITTED';
    currentPoints: number;
    pendingPoints: number | null;
    rejectionReason: string | null;
  }>;

  const rateByMilestone = useMemo(() => {
    const map = new Map<string, (typeof rates)[number]>();
    for (const r of rates) map.set(r.milestone, r);
    return map;
  }, [rates]);

  const {
    data: payouts = [],
    isLoading: payoutsLoading,
    error: payoutsError,
  } = useQuery({
    queryKey: ['admin-partner-payouts'],
    queryFn: async () => {
      const res = await referralPartnerApi.admin.payouts();
      return res.data?.data ?? [];
    },
  });

  const requestRateMut = useMutation({
    mutationFn: (milestone: 'NEW' | 'OPD_VISITED' | 'IPD_ADMITTED') =>
      referralPartnerApi.admin.hospital.requestRateChange({
        partnerId: selectedPartnerId,
        milestone,
        requestedPoints: Number(editRatePoints[milestone]),
        reason: rateReason || undefined,
      }),
    onSuccess: () => {
      toast.success('Rate change request sent for approval');
      qc.invalidateQueries({ queryKey: ['partner-approvals'] });
      qc.invalidateQueries({ queryKey: ['hospital-partner-rates', selectedPartnerId] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to request rate change'),
  });

  const requestLeadPointsMut = useMutation({
    mutationFn: () =>
      referralPartnerApi.admin.hospital.requestLeadPoints({
        leadId,
        milestone: leadMilestone,
        requestedPoints: Number(leadPoints),
        reason: leadReason || undefined,
      }),
    onSuccess: () => {
      toast.success('Lead points request sent for approval');
      setLeadId('');
      setLeadReason('');
      qc.invalidateQueries({ queryKey: ['partner-approvals'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to request lead points'),
  });

  const payoutApproveMut = useMutation({
    mutationFn: (id: string) => referralPartnerApi.admin.approvePayout(id),
    onSuccess: () => {
      toast.success('Payout approved');
      qc.invalidateQueries({ queryKey: ['admin-partner-payouts'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to approve payout'),
  });

  const payoutPaidMut = useMutation({
    mutationFn: ({ id, ref }: { id: string; ref: string }) =>
      referralPartnerApi.admin.markPaid(id, ref),
    onSuccess: () => {
      toast.success('Marked as paid');
      qc.invalidateQueries({ queryKey: ['admin-partner-payouts'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to mark paid'),
  });

  const rateMut = useMutation({
    mutationFn: () => referralPartnerApi.admin.setPayoutRate(Number(rupeePerPoint)),
    onSuccess: () => toast.success('Payout rate updated'),
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to update payout rate'),
  });

  const exportReportMut = useMutation({
    mutationFn: async () => {
      const res = await referralPartnerApi.admin.hospital.exportReport(
        reportFrom || undefined,
        reportTo || undefined
      );
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `partner_report_${reportFrom || 'from'}_to_${reportTo || 'to'}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Report export failed'),
  });

  const exportDetailedCsvMut = useMutation({
    mutationFn: async () => {
      const res = await referralPartnerApi.admin.hospital.exportDetailedCsv(
        reportFrom || undefined,
        reportTo || undefined
      );
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `partner_detailed_${reportFrom || 'from'}_to_${reportTo || 'to'}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Detailed CSV export failed'),
  });

  const topError =
    (partnersError as any)?.message ||
    (ratesError as any)?.message ||
    (payoutsError as any)?.message;

  const partnerOptions = useMemo(() => partners.map((p) => p.partner), [partners]);
  const selectedPartner = partnerOptions.find((p) => p.id === selectedPartnerId);

  return (
    <div className="p-6 space-y-8">
      <div>
        <h1 className="font-artis-serif text-2xl font-semibold text-artis-brown-dark">Partner management</h1>
        <p className="text-gray-500">Configure partner rates, payouts, and point override requests.</p>
      </div>

      {topError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {topError}
        </div>
      )}

      <section className="rounded-lg border bg-white p-4 space-y-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="w-full sm:w-96">
            <label className="text-xs text-gray-500">Select partner</label>
            <Select value={selectedPartnerId} onValueChange={setSelectedPartnerId}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder={partnersLoading ? 'Loading…' : 'Choose partner'} />
              </SelectTrigger>
              <SelectContent>
                {partnerOptions.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.firstName} {p.lastName} ({p.phone})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {selectedPartner && (
            <div className="text-sm text-gray-600">
              <div className="font-medium text-gray-900">
                {selectedPartner.firstName} {selectedPartner.lastName}
              </div>
              <div>{selectedPartner.email}</div>
            </div>
          )}
        </div>

        {!partnersLoading && partners.length === 0 && (
          <p className="text-sm text-artis-slate">No partner referrals found for this hospital yet.</p>
        )}

        <div className="mt-3 rounded-lg border bg-gray-50 p-3">
          <div className="text-sm font-medium text-gray-900">Download report</div>
          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-4 sm:items-end">
            <div>
              <label className="text-xs text-gray-500">From</label>
              <Input type="date" value={reportFrom} onChange={(e) => setReportFrom(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-gray-500">To</label>
              <Input type="date" value={reportTo} onChange={(e) => setReportTo(e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Button
                  className="w-full"
                  variant="outline"
                  onClick={() => exportReportMut.mutate()}
                  disabled={exportReportMut.isPending}
                >
                  {exportReportMut.isPending ? 'Preparing…' : 'Download ZIP'}
                </Button>
                <Button
                  className="w-full"
                  variant="outline"
                  onClick={() => exportDetailedCsvMut.mutate()}
                  disabled={exportDetailedCsvMut.isPending}
                >
                  {exportDetailedCsvMut.isPending ? 'Preparing…' : 'Download detailed CSV'}
                </Button>
              </div>
            </div>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Includes partners summary + leads + points within the selected time range for this hospital.
          </p>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="rounded-lg border bg-white p-4 space-y-3">
          <h2 className="text-lg font-medium">Partner default points</h2>
          {!selectedPartnerId ? (
            <p className="text-sm text-artis-slate">Select a partner to view and request changes.</p>
          ) : ratesLoading ? (
            <p className="text-sm text-artis-slate">Loading rates…</p>
          ) : (
            <div className="space-y-3">
              <div className="rounded-lg border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left">
                    <tr>
                      <th className="px-3 py-2">Milestone</th>
                      <th className="px-3 py-2">Current default</th>
                      <th className="px-3 py-2">New default (request)</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {(['NEW', 'OPD_VISITED', 'IPD_ADMITTED'] as const).map((m) => {
                      const r = rateByMilestone.get(m);
                      const current = r?.currentPoints ?? 0;
                      const pending = r?.pendingPoints;
                      const status = pending != null ? `PENDING (${pending})` : '—';
                      return (
                        <tr key={m}>
                          <td className="px-3 py-2 font-medium">{m}</td>
                          <td className="px-3 py-2">{current}</td>
                          <td className="px-3 py-2">
                            <Input
                              type="number"
                              value={editRatePoints[m]}
                              onChange={(e) =>
                                setEditRatePoints((prev) => ({ ...prev, [m]: e.target.value }))
                              }
                              placeholder={String(current)}
                              className="h-8 w-32"
                            />
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-500">{status}</td>
                          <td className="px-3 py-2">
                            <Button
                              size="sm"
                              onClick={() => {
                                if (!selectedPartnerId) return toast.error('Select a partner first');
                                if (!String(editRatePoints[m]).trim()) return toast.error('Enter points');
                                requestRateMut.mutate(m);
                              }}
                              disabled={requestRateMut.isPending}
                            >
                              Request change
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <Input
                placeholder="Reason (optional, applied to requests)"
                value={rateReason}
                onChange={(e) => setRateReason(e.target.value)}
              />
              <p className="text-xs text-gray-500">
                Hospital Admin can request changes. Super Admin approval makes them the future defaults for this partner.
              </p>
            </div>
          )}
        </section>

        <section className="rounded-lg border bg-white p-4 space-y-3">
          <h2 className="text-lg font-medium">Request lead-wise points override</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <label className="text-xs text-gray-500">Lead ID</label>
              <Input className="mt-1" value={leadId} onChange={(e) => setLeadId(e.target.value)} placeholder="Paste Lead ID" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Milestone</label>
              <Select value={leadMilestone} onValueChange={(v) => setLeadMilestone(v as any)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NEW">NEW</SelectItem>
                  <SelectItem value="OPD_VISITED">OPD_VISITED</SelectItem>
                  <SelectItem value="IPD_ADMITTED">IPD_ADMITTED</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-500">Requested points</label>
              <Input className="mt-1" type="number" value={leadPoints} onChange={(e) => setLeadPoints(e.target.value)} />
            </div>
            <div className="sm:col-span-2 flex items-end gap-3">
              <Input
                placeholder="Reason (optional)"
                value={leadReason}
                onChange={(e) => setLeadReason(e.target.value)}
                className="flex-1"
              />
              <Button
                onClick={() => {
                  if (!leadId.trim()) return toast.error('Lead ID is required');
                  requestLeadPointsMut.mutate();
                }}
                disabled={requestLeadPointsMut.isPending}
              >
                Send
              </Button>
            </div>
          </div>
          <p className="text-xs text-gray-500">Super Admin approval will update/create the ledger entry for that lead milestone.</p>
        </section>
      </div>

      <section className="rounded-lg border bg-white p-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-xs text-gray-500">₹ per point (payout)</label>
          <Input
            type="number"
            step="0.01"
            value={rupeePerPoint}
            onChange={(e) => setRupeePerPoint(e.target.value)}
            className="w-32 mt-1"
          />
        </div>
        <Button onClick={() => rateMut.mutate()} disabled={rateMut.isPending}>
          Save rate
        </Button>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-medium">Payout requests</h2>
        <div className="space-y-3">
          {(payouts as Array<{
            id: string;
            status: string;
            totalPoints: number;
            amountInr: number;
            partner: { firstName: string; lastName: string };
            hospital: { name: string };
          }>).map((p) => (
            <div key={p.id} className="rounded-lg border bg-white p-4 flex flex-wrap justify-between gap-3">
              <div>
                <p className="font-medium">
                  {p.partner.firstName} {p.partner.lastName} · {p.hospital.name}
                </p>
                <p className="text-sm text-artis-slate">
                  {p.totalPoints} pts · ₹{p.amountInr} · {p.status}
                </p>
              </div>
              <div className="flex gap-2 items-center">
                {p.status === 'REQUESTED' && (
                  <Button size="sm" variant="outline" onClick={() => payoutApproveMut.mutate(p.id)}>
                    Approve
                  </Button>
                )}
                {(p.status === 'REQUESTED' || p.status === 'APPROVED') && (
                  <>
                    <Input
                      placeholder="Payment ref"
                      value={payoutRefById[p.id] ?? ''}
                      onChange={(e) =>
                        setPayoutRefById((prev) => ({ ...prev, [p.id]: e.target.value }))
                      }
                      className="w-40 h-8"
                    />
                    <Button
                      size="sm"
                      onClick={() =>
                        payoutPaidMut.mutate({ id: p.id, ref: (payoutRefById[p.id] ?? '').trim() })
                      }
                      disabled={!(payoutRefById[p.id] ?? '').trim()}
                    >
                      Mark paid
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
          {payoutsLoading ? (
            <p className="text-gray-500">Loading…</p>
          ) : !payouts?.length ? (
            <p className="text-gray-500">No payout requests</p>
          ) : null}
        </div>
      </section>

    </div>
  );
}

